import { useEffect, useState, memo } from "react";
import { Box, Skeleton, Typography } from "@mui/material";

import { request } from "@/Api";
import { formatDateStringToDayMonthYear, formattedCurrency } from "@/utils";
import {
    StatusBadge,
    WidgetHeaderRow,
    ListRowDivider,
    ListRowItem,
    SeeAllRow,
} from "../primitives";

const getInvoiceStatus = (inv) => {
    if (parseFloat(inv.balance) === 0) return { label: "Paid", color: "green" };
    if (inv.days_late > 0) return { label: "Overdue", color: "orange" };
    return { label: "Sent", color: "grey" };
};

const OverdueInvoices = memo(({ personId }) => {
    const [invoices, setInvoices] = useState(null);

    useEffect(() => {
        const params = { offset: 0, limit: 10, orders: "+due_date" };
        if (personId) params.person_id = personId;
        request
            .get("/invoices", { params })
            .then((r) => setInvoices(r.data.items || []))
            .catch(() => setInvoices([]));
    }, [personId]);

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                height: "100%",
            }}
        >
            <WidgetHeaderRow
                title="Overdue Invoices"
                count={invoices?.length ?? null}
            />

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    flex: 1,
                    overflow: "auto",
                }}
            >
                {!invoices &&
                    [1, 2, 3, 4].map((i) => (
                        <Skeleton
                            key={i}
                            variant="rounded"
                            height={44}
                            sx={{
                                borderRadius: "6px",
                                bgcolor: "action.hover",
                            }}
                        />
                    ))}
                {invoices?.map((inv, i) => {
                    const status = getInvoiceStatus(inv);
                    return (
                        <Box
                            key={inv.id || i}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "4px",
                            }}
                        >
                            {i > 0 && <ListRowDivider />}
                            <ListRowItem
                                primary={formattedCurrency(
                                    inv.balance ?? inv.amount,
                                    inv.currency || "CAD",
                                )}
                                sub1={
                                    inv.due_date
                                        ? formatDateStringToDayMonthYear(
                                              inv.due_date,
                                          )
                                        : ""
                                }
                                sub2={inv.client_name || ""}
                                sub3={inv.project_name || ""}
                                right={
                                    <StatusBadge
                                        label={status.label}
                                        color={status.color}
                                    />
                                }
                            />
                        </Box>
                    );
                })}
                {invoices?.length === 0 && (
                    <Typography
                        sx={{
                            fontSize: 12,
                            color: "text.secondary",
                            fontFamily: "Inter Tight, sans-serif",
                            px: "8px",
                        }}
                    >
                        No invoices
                    </Typography>
                )}
            </Box>

            <SeeAllRow href="/invoices" label="All invoices" />
        </Box>
    );
});

OverdueInvoices.displayName = "OverdueInvoices";

export default OverdueInvoices;
